/**
 * Central registry of localStorage keys that hold business-account-scoped,
 * TRANSIENT UI state — cross-page navigation handoffs like "open this
 * conversation when the Inbox mounts". These values reference entities that
 * belong to ONE business account (conversation ids, contact ids), so they
 * must be wiped when the active business account changes; otherwise the
 * Inbox on Account B tries to open a conversation from Account A.
 *
 * Cleared centrally by AccountScopeBoundary on every account switch.
 *
 * What does NOT belong here:
 * - Global prefs (theme, sidebar state, `kanban_cards_per_column`, tour keys).
 * - Auth/session keys (`auth_token`, `selectedBusinessAccountId`, ...).
 * - ORGANIZATION-scoped selections — `instagram:selected-account-id`,
 *   `youtube:selected-channel-id`, `ig-flow-builder:selected-account`,
 *   `fb-flow-builder:selected-account`. Social accounts hang off the org,
 *   not the WhatsApp business account, so they legitimately survive a
 *   business-account switch. Adding them here would be a regression.
 */
export const ACCOUNT_SCOPED_TRANSIENT_KEYS = [
  'selectedConversationId',
  'tempContactName',
  'tempContactPhone',
  'tempContactId',
] as const;

/** Remove every transient account-scoped key. Safe to call repeatedly. */
export function clearAccountScopedStorage(): void {
  if (typeof window === 'undefined') return;
  for (const key of ACCOUNT_SCOPED_TRANSIENT_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable (private mode quirks) — never throw here.
    }
  }
}

/**
 * Build a localStorage key namespaced by business account id, for UI state
 * that should INTENTIONALLY persist per account instead of being reset on
 * switch (e.g. a per-account column layout). Each account then reads/writes
 * its own independent entry, so nothing leaks across accounts.
 *
 * Usage: localStorage.setItem(accountStorageKey('leads:columns', baId), ...)
 */
export function accountStorageKey(baseKey: string, accountId: string | null | undefined): string {
  return `${baseKey}::ba:${accountId || 'none'}`;
}
